import React, { useState, useCallback, useMemo } from 'react';
import { useThreatProfile } from '@/hooks/useThreatProfile';
import ThreatProfileControls from '@/components/features/threatProfile/ThreatProfileControls';
import ActorSelectionPanel from '@/components/features/threatProfile/ActorSelectionPanel';
import VisualizationPanel from '@/components/features/threatProfile/VisualizationPanel';
import DescriptionPanel from '@/components/features/threatProfile/DescriptionPanel';
import { Target, Terminal, Activity, ShieldAlert, Zap, Skull } from 'lucide-react';

const ThreatProfile: React.FC = () => {
    // Extract properties from the hook
    const {
        selectedActor,
        searchQuery,
        severityFilter,
        viewMode,
        filteredActors,
        selectedActorData,
        totalActors,
        setSelectedActor,
        setSearchQuery,
        setSeverityFilter,
        setViewMode,
    } = useThreatProfile();

    // 1. Local state to resolve TS(2339) errors for iframe error tracking
    const [fileLoadErrors, setFileLoadErrors] = useState<Set<string>>(new Set());

    const handleRetryLoad = useCallback((actorId: string) => {
        setFileLoadErrors(prev => {
            const newSet = new Set(prev);
            newSet.delete(actorId);
            return newSet;
        });
    }, []);

    // 2. Data Aggregation (Inspired by threatFeed.ts Stats)
    const threatStats = useMemo(() => {
        let criticalCount = 0;
        let highCount = 0;
        const uniqueTechniques = new Set<string>();

        filteredActors.forEach(actor => {
            if (actor.severity === 'critical') criticalCount++;
            if (actor.severity === 'high') highCount++;
            if (actor.techniques) {
                actor.techniques.forEach(t => {
                    // Safely extract the ID whether it's the old string format or new object format
                    const techId = typeof t === 'string' ? t : (t as unknown as { id: string }).id;
                    if (techId) uniqueTechniques.add(techId);
                });
            }
        });

        return {
            total: filteredActors.length,
            critical: criticalCount,
            high: highCount,
            techniques: uniqueTechniques.size
        };
    }, [filteredActors]);

    return (
        <div className="min-h-screen bg-black text-slate-300 font-mono selection:bg-[#02DFC2]/30 selection:text-white pb-12">
            <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">

                {/* --- HEADER SECTION --- */}
                <div className="mb-6 relative">
                    <div className="flex flex-col space-y-2 mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-[#02DFC2]/10 border border-[#02DFC2]/20 rounded-xl shadow-[inset_0_0_15px_rgba(2,223,194,0.1)]">
                                <Target className="w-6 h-6 text-[#02DFC2]" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white tracking-tight uppercase flex items-center gap-3">
                                    Threat Actor Intelligence
                                </h2>
                                <div className="flex items-center gap-2 mt-1">
                                    <Terminal className="w-3.5 h-3.5 text-slate-500" />
                                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-bold">
                                        MITRE ATT&CK® Group Evaluation & TTP Mapping
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- DYNAMIC METRICS RIBBON --- */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] p-4 rounded-xl shadow-lg flex items-center gap-4">
                            <div className="p-2 bg-blue-500/10 rounded-lg"><Activity className="w-5 h-5 text-blue-500" /></div>
                            <div>
                                <div className="text-2xl font-bold text-white">{threatStats.total}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Tracked Actors</div>
                            </div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] p-4 rounded-xl shadow-lg flex items-center gap-4">
                            <div className="p-2 bg-[#FF3333]/10 rounded-lg"><Skull className="w-5 h-5 text-[#FF3333]" /></div>
                            <div>
                                <div className="text-2xl font-bold text-[#FF3333]">{threatStats.critical}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Critical Threat Level</div>
                            </div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] p-4 rounded-xl shadow-lg flex items-center gap-4">
                            <div className="p-2 bg-orange-500/10 rounded-lg"><ShieldAlert className="w-5 h-5 text-orange-500" /></div>
                            <div>
                                <div className="text-2xl font-bold text-orange-500">{threatStats.high}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-widest">High Threat Level</div>
                            </div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] p-4 rounded-xl shadow-lg flex items-center gap-4">
                            <div className="p-2 bg-[#EAB308]/10 rounded-lg"><Zap className="w-5 h-5 text-[#EAB308]" /></div>
                            <div>
                                <div className="text-2xl font-bold text-[#EAB308]">{threatStats.techniques.toLocaleString()}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Unique TTPs Mapped</div>
                            </div>
                        </div>
                    </div>

                    {/* Controls Component - Cyber Styling */}
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] p-3 rounded-2xl shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent opacity-30"></div>
                        <ThreatProfileControls
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            severityFilter={severityFilter}
                            onSeverityFilterChange={setSeverityFilter}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                        />
                    </div>
                </div>

                {/* --- PRIMARY INTELLIGENCE GRID --- */}
                <div className="grid grid-cols-12 gap-6 h-[calc(100vh-450px)] min-h-[600px] mb-6">

                    {/* Left Column: Actor Selection (4/12) */}
                    <div className="col-span-12 lg:col-span-4 xl:col-span-3 h-full overflow-hidden bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl shadow-2xl relative group">
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent opacity-50 shadow-[0_0_10px_#02DFC2]"></div>
                        <ActorSelectionPanel
                            actors={filteredActors}
                            totalActors={totalActors}
                            selectedActor={selectedActor}
                            viewMode={viewMode}
                            onActorSelect={setSelectedActor}
                        />
                    </div>

                    {/* Right Column: Visualization (8/12) */}
                    <div className="col-span-12 lg:col-span-8 xl:col-span-9 h-full overflow-hidden bg-[#050505] border border-[#1A1D24] rounded-2xl shadow-2xl relative">
                        {/* Authentic Cyber Grid Overlay */}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:32px_32px]"></div>

                        <VisualizationPanel
                            selectedActor={selectedActor}
                            selectedActorData={selectedActorData}
                            fileLoadErrors={fileLoadErrors}
                            onRetryLoad={handleRetryLoad}
                        />
                    </div>
                </div>

                {/* --- BOTTOM ROW: DETAILED INTELLIGENCE DESCRIPTION --- */}
                {selectedActorData && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#02DFC2] to-transparent opacity-20"></div>
                        <DescriptionPanel actor={selectedActorData} />
                    </div>
                )}
            </div>

            {/* Global Page Scrollbar Overrides */}
            <style dangerouslySetInnerHTML={{
                __html: `
                body {
                    background-color: #000000;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 5px;
                    height: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #1A1D24;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(2, 223, 194, 0.3);
                }
            `}} />
        </div>
    );
};

export default ThreatProfile;